import mammoth from "mammoth";
import ExcelJS from "exceljs";
import * as CFB from "cfb";
import DOMPurify from "dompurify";

export const DOC_MIME = "application/msword";
export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLS_MIME = "application/vnd.ms-excel";

const MAX_SHEETS = 10;
const MAX_CSV_ROWS = 500;

export function isDocMime(mimeType) {
  return normalizeMime(mimeType) === DOC_MIME;
}

export function isDocxMime(mimeType) {
  return normalizeMime(mimeType) === DOCX_MIME;
}

export function isSpreadsheetMime(mimeType) {
  const mime = normalizeMime(mimeType);
  return mime === XLSX_MIME || mime === XLS_MIME;
}

export function isCsvMime(mimeType) {
  const mime = normalizeMime(mimeType);
  return (
    mime === "text/csv" ||
    mime === "application/csv" ||
    mime === "text/comma-separated-values"
  );
}

/** Alias for spreadsheet MIME check (xls + xlsx). */
export function isXlsxMime(mimeType) {
  return isSpreadsheetMime(mimeType);
}

export function isOfficePreviewMime(mimeType) {
  return (
    isDocMime(mimeType) ||
    isDocxMime(mimeType) ||
    isSpreadsheetMime(mimeType)
  );
}

function normalizeMime(mimeType) {
  return String(mimeType || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
}

function sanitizeHtml(html) {
  return DOMPurify.sanitize(String(html || ""), {
    USE_PROFILES: { html: true },
  });
}

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function textToHtmlParagraphs(text) {
  const parts = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return "<p><em>(Empty document)</em></p>";
  return parts.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
}

function asUint8(content) {
  if (!content) return new Uint8Array();
  if (content instanceof Uint8Array) return content;
  if (ArrayBuffer.isView(content)) {
    return new Uint8Array(content.buffer, content.byteOffset, content.byteLength);
  }
  return new Uint8Array(content);
}

function readU32LE(bytes, offset) {
  return (
    bytes[offset] |
    (bytes[offset + 1] << 8) |
    (bytes[offset + 2] << 16) |
    (bytes[offset + 3] << 24)
  ) >>> 0;
}

function readU16LE(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

/** CP1252-ish mapping used by legacy Word ANSI pieces (subset). */
function binaryToUnicode(binary) {
  let out = "";
  for (let i = 0; i < binary.length; i++) {
    const c = binary.charCodeAt(i) & 0xff;
    if (c === 0x0d || c === 0x07 || c === 0x0b) {
      out += "\n";
    } else if (c >= 0x20 || c === 0x09) {
      out += String.fromCharCode(c);
    }
  }
  return out;
}

function decodeUcs2(bytes, start, length) {
  let out = "";
  const end = Math.min(bytes.length, start + length);
  for (let i = start; i + 1 < end; i += 2) {
    const code = bytes[i] | (bytes[i + 1] << 8);
    if (code === 0x000d || code === 0x0007 || code === 0x000b) {
      out += "\n";
    } else if (code === 0 || code === 0xffff) {
      // skip
    } else {
      out += String.fromCharCode(code);
    }
  }
  return out;
}

/**
 * Extract plain text from a legacy Word .doc (OLE) via the piece table.
 * Fidelity is limited (no complex formatting); good enough for in-app preview.
 */
export function extractDocPlainText(arrayBuffer) {
  const cfb = CFB.read(new Uint8Array(arrayBuffer), { type: "array" });
  const wordEntry = CFB.find(cfb, "WordDocument");
  if (!wordEntry?.content) {
    throw new Error("Unable to read this .doc file");
  }
  const word = asUint8(wordEntry.content);
  if (word.length < 0x1a6) {
    throw new Error("Corrupt or unsupported .doc file");
  }

  const flags = readU16LE(word, 0x000a);
  const tableName = (flags & 0x0200) !== 0 ? "1Table" : "0Table";
  const tableEntry = CFB.find(cfb, tableName);
  if (!tableEntry?.content) {
    throw new Error("Unable to read .doc table stream");
  }
  const table = asUint8(tableEntry.content);

  // ccpText — main document character count (FIB)
  const ccpText = readU32LE(word, 0x004c);
  let pos = readU32LE(word, 0x01a2);

  while (pos < table.length) {
    const flag = table[pos];
    if (flag !== 1) break;
    pos += 1;
    if (pos + 2 > table.length) break;
    const skip = readU16LE(table, pos);
    pos += 2 + skip;
  }

  if (pos >= table.length || table[pos] !== 2) {
    throw new Error("Unsupported .doc piece table");
  }
  pos += 1;
  if (pos + 4 > table.length) {
    throw new Error("Corrupt .doc piece table");
  }
  const pieceTableSize = readU32LE(table, pos);
  pos += 4;
  const pieces = (pieceTableSize - 4) / 12;
  if (!Number.isFinite(pieces) || pieces < 1 || pieces > 100000) {
    throw new Error("Corrupt .doc piece table");
  }

  let text = "";
  for (let x = 0; x < pieces; x++) {
    const cpOffset = pos + x * 4;
    const descOffset = pos + (pieces + 1) * 4 + x * 8 + 2;
    if (descOffset + 4 > table.length || cpOffset + 8 > table.length) break;

    let startFilePos = readU32LE(table, descOffset);
    const unicode = (startFilePos & 0x40000000) === 0;
    if (!unicode) {
      startFilePos = Math.floor((startFilePos & ~0x40000000) / 2);
    }
    const lStart = readU32LE(table, pos + x * 4);
    const lEnd = readU32LE(table, pos + (x + 1) * 4);
    const totLength = lEnd - lStart;
    if (totLength <= 0) continue;

    const byteLen = totLength * (unicode ? 2 : 1);
    if (startFilePos + byteLen > word.length) continue;

    if (unicode) {
      text += decodeUcs2(word, startFilePos, byteLen);
    } else {
      let binary = "";
      for (let i = 0; i < byteLen; i++) {
        binary += String.fromCharCode(word[startFilePos + i]);
      }
      text += binaryToUnicode(binary);
    }

    // Stop after main document text when we know its length
    if (ccpText > 0 && text.replace(/\n/g, "").length >= ccpText) {
      break;
    }
  }

  text = text
    // Strip NUL bytes from binary-extracted DOC text (control char in regex).
    // eslint-disable-next-line no-control-regex
    .replace(/\u0000/g, "")
    .replace(/[^\S\n]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (!text) {
    throw new Error("No readable text found in .doc");
  }

  // Prefer main body length when FIB reports it
  if (ccpText > 0 && text.length > ccpText * 2) {
    text = text.slice(0, ccpText);
  }

  return text;
}

/**
 * Convert a DOCX ArrayBuffer to sanitized HTML.
 * @returns {Promise<string>}
 */
export async function convertDocxToHtml(arrayBuffer) {
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return sanitizeHtml(result.value);
}

/**
 * Convert a legacy .doc ArrayBuffer to sanitized HTML (text preview).
 * @returns {Promise<string>}
 */
export async function convertDocToHtml(arrayBuffer) {
  const text = extractDocPlainText(arrayBuffer);
  return sanitizeHtml(textToHtmlParagraphs(text));
}

/**
 * Convert an XLSX ArrayBuffer to sheet HTML previews (capped).
 * Legacy .xls (BIFF) is not supported by ExcelJS; callers should surface the error.
 * @returns {Promise<{ name: string, html: string }[]>}
 */
export async function convertXlsxToSheets(arrayBuffer) {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  return workbookToSheets(workbook);
}

/**
 * Convert CSV text to a single-sheet HTML table preview.
 * @returns {{ name: string, html: string }[]}
 */
export function convertCsvToSheets(csvText) {
  const rows = parseCsvRows(String(csvText || "")).slice(0, MAX_CSV_ROWS);
  const html = rowsToHtmlTable(rows);
  return [{ name: "Sheet1", html: sanitizeHtml(html) }];
}

function workbookToSheets(workbook, { maxRows = null } = {}) {
  const sheets = [];
  workbook.eachSheet((worksheet) => {
    if (sheets.length >= MAX_SHEETS) return;
    sheets.push({
      name: worksheet.name || `Sheet${sheets.length + 1}`,
      html: sanitizeHtml(worksheetToHtml(worksheet, maxRows)),
    });
  });
  return sheets;
}

function cellDisplayValue(cell) {
  if (!cell || cell.value == null) return "";
  if (cell.text != null && cell.text !== "") return String(cell.text);
  const value = cell.value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    if (value.richText) {
      return value.richText.map((part) => part.text || "").join("");
    }
    if (value.text != null) return String(value.text);
    if (value.result != null) return String(value.result);
    if (value.formula != null) return String(value.formula);
  }
  return String(value);
}

function worksheetToHtml(worksheet, maxRows) {
  const rows = [];
  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (maxRows != null && rowNumber > maxRows) return;
    const cells = [];
    const colCount = Math.max(row.cellCount || 0, worksheet.columnCount || 0);
    for (let c = 1; c <= colCount; c++) {
      cells.push(cellDisplayValue(row.getCell(c)));
    }
    rows.push(cells);
  });
  return rowsToHtmlTable(rows);
}

function rowsToHtmlTable(rows) {
  if (!rows.length) return "<table><tbody><tr><td><em>(Empty)</em></td></tr></tbody></table>";
  const body = rows
    .map(
      (cols) =>
        `<tr>${cols
          .map((col) => `<td>${escapeHtml(col)}</td>`)
          .join("")}</tr>`
    )
    .join("");
  return `<table><tbody>${body}</tbody></table>`;
}

/** Minimal RFC4180-ish CSV parser for preview (quoted fields, commas, newlines). */
function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let field = "";
  let i = 0;
  let inQuotes = false;
  const s = text.replace(/^\uFEFF/, "");
  while (i < s.length) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(field);
      field = "";
      i += 1;
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && s[i + 1] === "\n") i += 1;
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
}
